// Phantom Industries — client-side catalog + helpers.
// Products & tiers stay on the frontend for instant render.
// Orders + Contact go to the backend via /api/*.

export const LOGO_URL = "/logo-clean.png";

export const MOODBOARD_URL =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_phantom-os-shop/artifacts/boogujvt_ChatGPT%20Image%20Jul%2023%2C%202026%2C%2003_21_52%20PM.png";

// Tier wordmark logos supplied by the store owner.
// Each is deliberately styled to match the tier's personality:
//   Degoogled \u2192 playful Google-parody multi-colour
//   Stealth   \u2192 bold minimal black-on-white
//   Phantom   \u2192 italic purple-outline (matches brand)
export const TIER_LOGOS = {
  degoogled: "/img/tiers/degoogled.png",
  stealth:   "https://customer-assets-gfyr7b9c.emergentagent.net/job_phantom-os-shop/artifacts/u2ca7fp7_2197773.png",
  phantom:   "/img/tiers/phantom.png",
};

// Design styles available on merch and phone cases. Customer picks a style
// at checkout time; the reference sheet (MOODBOARD_URL) shows all options.
export const DESIGN_STYLES = [
  { id: "drip-classic",   label: "Drip \u2014 Classic White" },
  { id: "drip-purple",    label: "Drip \u2014 Purple Bleed" },
  { id: "bold-outline",   label: "Bold Outline" },
  { id: "grunge-stencil", label: "Grunge Stencil" },
  { id: "brush-slash",    label: "Brush Slash" },
  { id: "varsity",        label: "Varsity Arc" },
  { id: "slasher-purple", label: "Slasher Purple" },
  { id: "gothic-heavy",   label: "Gothic Heavy" },
  { id: "skull-bracket",  label: "Skull Bracket" },
  { id: "italic-mark",    label: "Italic Mark" },
  { id: "streak-italic",  label: "Streak Italic" },
  { id: "padlock-lock",   label: "Padlock Lock-up" },
  { id: "icon-hooded",    label: "Icon \u2014 Hooded Figure" },
  { id: "icon-skull",     label: "Icon \u2014 Dripping Skull" },
  { id: "icon-padlock",   label: "Icon \u2014 Purple Padlock" },
];

export const HERO_IMAGES = {
  cyber: "https://images.unsplash.com/photo-1614064548237-096f735f344f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  hooded: "https://images.unsplash.com/photo-1771061863061-8ffdddb28098?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  darkTech: "https://images.unsplash.com/photo-1536242918817-db5e93c7a0e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  padlock: "https://images.pexels.com/photos/5380669/pexels-photo-5380669.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000",
};

const PIXEL_IMG = [
  "https://images.unsplash.com/photo-1598965402089-897ce52e8355?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1724341039339-036842055cae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1612442443556-09b5b309e637?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.unsplash.com/photo-1724322535079-11b08f7f5c88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  "https://images.pexels.com/photos/20716652/pexels-photo-20716652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1400",
  "https://images.pexels.com/photos/32218867/pexels-photo-32218867.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1400",
];

export const TIERS = [
  {
    id: "degoogled",
    name: "Degoogled",
    tagline: "Tier I — Freedom from Google.",
    price_add: 450,
    color: "#a1a1aa",
    glow: "tier-glow-1",
    highlights: [
      "GrapheneOS clean install",
      "Zero Google services or telemetry",
      "F-Droid + Aurora Store pre-configured",
      "Baseline hardening profile",
      "Encrypted storage by default",
    ],
    description:
      "The essential privacy build. We strip every Google identifier, telemetry hook and tracking service from your device and hand you back a phone that answers only to you.",
  },
  {
    id: "stealth",
    name: "Stealth",
    tagline: "Tier II — Off the map.",
    price_add: 750,
    color: "#a855f7",
    glow: "tier-glow-2",
    highlights: [
      "Everything in Degoogled",
      "MAC + IMEI randomization on boot",
      "Trusted DNS-over-HTTPS resolver",
      "Hardened encrypted messaging apps",
      "Encrypted VPN tunnel pre-installed",
      "Duress PIN & panic wipe",
    ],
    description:
      "Built for people who don't want to be found. Stealth adds active anti-fingerprint, network cloaking and hardened messaging on top of a fully degoogled base.",
    popular: true,
  },
  {
    id: "phantom",
    name: "Phantom",
    tagline: "Tier III — Untraceable.",
    price_add: 1250,
    color: "#c084fc",
    glow: "tier-glow-3",
    highlights: [
      "Everything in Stealth",
      "Custom hardened GrapheneOS profile",
      "Multi-profile compartmentalization",
      "Hidden vault profile + decoy mode",
      "Hardware sensor kill-switches",
      "Self-destruct duress protocol",
      "Encrypted cloud key-escrow",
      "Lifetime priority support",
    ],
    description:
      "The end-of-the-line build. Every attack surface is closed, every sensor gated, every profile compartmentalized. Reserved for people who cannot afford to be seen.",
  },
];

// GrapheneOS verified-boot auditor fingerprints supplied by the store owner.
// Displayed on the product page as a trust signal.
export const BOOT_KEYS = {
  "pixel-10-pro-fold": "55a...ee3",
  "pixel-10-pro-xl": "141...f74",
  "pixel-10-pro": "4e8...093",
  "pixel-10": "3f7...2c7",
  "pixel-10a": "d8f...f80",
  "pixel-9-pro-fold": "af4...24c",
  "pixel-9-pro-xl": "55d...023",
  "pixel-9-pro": "f72...e86",
  "pixel-9": "9e6...2de",
  "pixel-9a": "050...45b",
  "pixel-8-pro": "896...c1c",
  "pixel-8": "cd7...233",
  "pixel-8a": "096...c5e",
  "pixel-fold": "ee0...842",
  "pixel-tablet": "94d...230",
  "pixel-7-pro": "bc1...82f",
  "pixel-7": "3ef...fa1",
  "pixel-7a": "508...1e4",
  "pixel-6-pro": "439...25c",
  "pixel-6": "f0a...7e8",
  "pixel-6a": "08c...031",
};

// Full lineup with Australian retail base prices.
export const PIXELS = [
  // ---- Pixel 10 series ----
  {
    id: "pixel-10-pro-fold",
    name: "Pixel 10 Pro Fold",
    year: 2025,
    display: '8.0" LTPO OLED foldable · 120 Hz',
    chip: "Google Tensor G5",
    ram: "16 GB",
    storage: ["256 GB", "512 GB", "1 TB"],
    battery: "5015 mAh",
    base_aud: 2999,
    image: PIXEL_IMG[0],
    tagline: "A folding fortress.",
  },
  {
    id: "pixel-10-pro-xl",
    name: "Pixel 10 Pro XL",
    year: 2025,
    display: '6.8" LTPO OLED · 120 Hz',
    chip: "Google Tensor G5",
    ram: "16 GB",
    storage: ["256 GB", "512 GB", "1 TB"],
    battery: "5200 mAh",
    base_aud: 1999,
    image: PIXEL_IMG[1],
    tagline: "The new Phantom flagship.",
  },
  {
    id: "pixel-10-pro",
    name: "Pixel 10 Pro",
    year: 2025,
    display: '6.3" LTPO OLED · 120 Hz',
    chip: "Google Tensor G5",
    ram: "16 GB",
    storage: ["128 GB", "256 GB", "512 GB"],
    battery: "4870 mAh",
    base_aud: 1699,
    image: PIXEL_IMG[2],
    tagline: "Pro power. Ghost profile.",
  },
  {
    id: "pixel-10",
    name: "Pixel 10",
    year: 2025,
    display: '6.3" OLED · 120 Hz',
    chip: "Google Tensor G5",
    ram: "12 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4970 mAh",
    base_aud: 1349,
    image: PIXEL_IMG[3],
    tagline: "Modern silence.",
  },
  {
    id: "pixel-10a",
    name: "Pixel 10a",
    year: 2025,
    display: '6.1" OLED · 120 Hz',
    chip: "Google Tensor G5",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4600 mAh",
    base_aud: 849,
    image: PIXEL_IMG[4],
    tagline: "The everyday Phantom.",
  },

  // ---- Pixel 9 series ----
  {
    id: "pixel-9-pro-fold",
    name: "Pixel 9 Pro Fold",
    year: 2024,
    display: '8.0" LTPO OLED foldable · 120 Hz',
    chip: "Google Tensor G4",
    ram: "16 GB",
    storage: ["256 GB", "512 GB"],
    battery: "4650 mAh",
    base_aud: 2699,
    image: PIXEL_IMG[5],
    tagline: "Foldable privacy, executive class.",
  },
  {
    id: "pixel-9-pro-xl",
    name: "Pixel 9 Pro XL",
    year: 2024,
    display: '6.8" LTPO OLED · 120 Hz',
    chip: "Google Tensor G4",
    ram: "16 GB",
    storage: ["256 GB", "512 GB", "1 TB"],
    battery: "5060 mAh",
    base_aud: 1849,
    image: PIXEL_IMG[0],
    tagline: "Flagship canvas. Phantom-grade.",
  },
  {
    id: "pixel-9-pro",
    name: "Pixel 9 Pro",
    year: 2024,
    display: '6.3" LTPO OLED · 120 Hz',
    chip: "Google Tensor G4",
    ram: "16 GB",
    storage: ["128 GB", "256 GB", "512 GB"],
    battery: "4700 mAh",
    base_aud: 1699,
    image: PIXEL_IMG[1],
    tagline: "Pro in every dimension.",
  },
  {
    id: "pixel-9",
    name: "Pixel 9",
    year: 2024,
    display: '6.3" OLED · 120 Hz',
    chip: "Google Tensor G4",
    ram: "12 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4700 mAh",
    base_aud: 1349,
    image: PIXEL_IMG[2],
    tagline: "Effortless power. Silent presence.",
  },
  {
    id: "pixel-9a",
    name: "Pixel 9a",
    year: 2025,
    display: '6.3" OLED · 120 Hz',
    chip: "Google Tensor G4",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "5100 mAh",
    base_aud: 849,
    image: PIXEL_IMG[3],
    tagline: "Battery king. Locked down.",
  },

  // ---- Pixel 8 series ----
  {
    id: "pixel-8-pro",
    name: "Pixel 8 Pro",
    year: 2023,
    display: '6.7" LTPO OLED · 120 Hz',
    chip: "Google Tensor G3",
    ram: "12 GB",
    storage: ["128 GB", "256 GB", "512 GB"],
    battery: "5050 mAh",
    base_aud: 1699,
    image: PIXEL_IMG[4],
    tagline: "Pro-class privacy champion.",
  },
  {
    id: "pixel-8",
    name: "Pixel 8",
    year: 2023,
    display: '6.2" OLED · 120 Hz',
    chip: "Google Tensor G3",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4575 mAh",
    base_aud: 1199,
    image: PIXEL_IMG[5],
    tagline: "Compact. Compartmentalized.",
  },
  {
    id: "pixel-8a",
    name: "Pixel 8a",
    year: 2024,
    display: '6.1" OLED · 120 Hz',
    chip: "Google Tensor G3",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4492 mAh",
    base_aud: 849,
    image: PIXEL_IMG[0],
    tagline: "Entry point to true privacy.",
  },
  {
    id: "pixel-fold",
    name: "Pixel Fold",
    year: 2023,
    display: '7.6" OLED foldable · 120 Hz',
    chip: "Google Tensor G2",
    ram: "12 GB",
    storage: ["256 GB", "512 GB"],
    battery: "4821 mAh",
    base_aud: 2699,
    image: PIXEL_IMG[1],
    tagline: "The original folding ghost.",
  },
  {
    id: "pixel-tablet",
    name: "Pixel Tablet",
    year: 2023,
    display: '11" LCD · 60 Hz',
    chip: "Google Tensor G2",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "27 Wh",
    base_aud: 899,
    image: PIXEL_IMG[2],
    tagline: "A tablet Google can't see.",
  },

  // ---- Pixel 7 series ----
  {
    id: "pixel-7-pro",
    name: "Pixel 7 Pro",
    year: 2022,
    display: '6.7" LTPO OLED · 120 Hz',
    chip: "Google Tensor G2",
    ram: "12 GB",
    storage: ["128 GB", "256 GB", "512 GB"],
    battery: "5000 mAh",
    base_aud: 1299,
    image: PIXEL_IMG[3],
    tagline: "A legendary flagship, quietly re-armed.",
  },
  {
    id: "pixel-7",
    name: "Pixel 7",
    year: 2022,
    display: '6.3" OLED · 90 Hz',
    chip: "Google Tensor G2",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4355 mAh",
    base_aud: 999,
    image: PIXEL_IMG[4],
    tagline: "Battle-tested. Reforged for privacy.",
  },
  {
    id: "pixel-7a",
    name: "Pixel 7a",
    year: 2023,
    display: '6.1" OLED · 90 Hz',
    chip: "Google Tensor G2",
    ram: "8 GB",
    storage: ["128 GB"],
    battery: "4385 mAh",
    base_aud: 749,
    image: PIXEL_IMG[5],
    tagline: "Small. Fast. Ghost-quiet.",
  },

  // ---- Pixel 6 series ----
  {
    id: "pixel-6-pro",
    name: "Pixel 6 Pro",
    year: 2021,
    display: '6.7" LTPO OLED · 120 Hz',
    chip: "Google Tensor",
    ram: "12 GB",
    storage: ["128 GB", "256 GB", "512 GB"],
    battery: "5003 mAh",
    base_aud: 1299,
    image: PIXEL_IMG[0],
    tagline: "Classic Pro, modern hardening.",
  },
  {
    id: "pixel-6",
    name: "Pixel 6",
    year: 2021,
    display: '6.4" OLED · 90 Hz',
    chip: "Google Tensor",
    ram: "8 GB",
    storage: ["128 GB", "256 GB"],
    battery: "4614 mAh",
    base_aud: 999,
    image: PIXEL_IMG[1],
    tagline: "The first Tensor. Still fast. Still ours.",
  },
  {
    id: "pixel-6a",
    name: "Pixel 6a",
    year: 2022,
    display: '6.1" OLED · 60 Hz',
    chip: "Google Tensor",
    ram: "6 GB",
    storage: ["128 GB"],
    battery: "4410 mAh",
    base_aud: 599,
    image: PIXEL_IMG[2],
    tagline: "The most accessible Phantom.",
  },
];

export const FEATURES = [
  {
    title: "GrapheneOS at the core",
    body: "Every Phantom Industries runs the world's most hardened mobile OS — audited, minimal, and free of Google.",
  },
  {
    title: "Hardware you can trust",
    body: "Google Pixel devices ship with verified boot and a Titan M2 secure element. We lock them down further.",
  },
  {
    title: "Zero-knowledge setup",
    body: "Your phone leaves us with no accounts, no identifiers and no fingerprints tied to you. Ever.",
  },
  {
    title: "Panic and duress",
    body: "Enter a duress PIN to wipe or hide. Physical kill-switches gate the mic, camera and radios.",
  },
];

export const SECURITY_FAQS = [
  {
    q: "Is GrapheneOS actually more secure than iOS or stock Android?",
    a: "Yes. GrapheneOS uses a hardened memory allocator, better sandboxing, per-app sensor and network permissions, MAC randomization, verified boot with the Titan M2 secure element, and it removes Google Play Services from the trusted computing base. It's the OS that Edward Snowden publicly uses.",
  },
  {
    q: "What data do you keep about me when I buy a Phantom Industries?",
    a: "Only what's needed for shipping and warranty: name, delivery address, email, phone (optional). We do not run analytics on our site, we do not use cookies for tracking, and we do not share your data with any third party. Your record is deleted 30 days after successful delivery, keeping only the anonymized order number for warranty lookup.",
  },
  {
    q: "Can law enforcement compel you to unlock my phone?",
    a: "No. Your device leaves us with no accounts and no keys retained by us. Only you know the passphrase you set on first boot. We have no technical ability to unlock a device once it's in your hands — that's the point.",
  },
  {
    q: "What if I forget my passphrase?",
    a: "We cannot recover it. Full-disk encryption means without the passphrase the data is cryptographically unrecoverable, and we designed it that way on purpose. Write your passphrase on paper and store it somewhere physical. Phantom-tier customers can optionally use encrypted cloud key-escrow.",
  },
  {
    q: "How do I know you haven't installed a backdoor?",
    a: "Every device ships with its GrapheneOS Auditor fingerprint printed on the receipt. On first boot you cross-check it against the fingerprint listed on this website. If they don't match, your device may have been tampered with — do not use it and contact us. We also publish the diff for our Phantom-tier configuration profile.",
  },
  {
    q: "Can my ISP or mobile carrier see what I'm doing?",
    a: "Your carrier can see that a device on their network is connected — that's inherent to mobile telephony. They cannot see the decrypted content of any modern encrypted messenger. On Stealth and Phantom tiers, MAC and IMEI randomization plus DNS-over-HTTPS and an encrypted VPN make it hard to track the device across networks.",
  },
  {
    q: "What about the cellular modem / baseband?",
    a: "The baseband is a closed component on every modern smartphone — Pixel included. GrapheneOS isolates the baseband as well as any hardware currently allows. If your threat model includes state-level baseband exploits, keep the phone in a Faraday pouch (we sell one) or in airplane mode when not actively making calls.",
  },
  {
    q: "How do system updates work? Doesn't Google push updates?",
    a: "GrapheneOS ships its own signed over-the-air updates independent of Google. Your device verifies the signature against the GrapheneOS public key before installing anything. Nothing about the update path touches Google's servers.",
  },
  {
    q: "What about location tracking?",
    a: "Google's Network Location service is disabled by default on GrapheneOS. GPS on Pixel is client-only — the device computes its position from satellite signals and does not send anything back. Apps only see location if you explicitly grant permission per session.",
  },
  {
    q: "What happens if my device is seized or stolen?",
    a: "Set a duress PIN during setup. Enter it and the device either wipes or switches into a decoy profile (Phantom tier). Storage is encrypted at rest, so even a forensic image of the device is useless without your passphrase.",
  },
  {
    q: "Do you disclose orders under subpoena?",
    a: "We comply with lawful Australian orders like any other Australian business. The most useful thing about our data-minimization is that we simply do not hold much — we cannot disclose data we do not retain. Once your 30-day warranty window closes, we no longer have your delivery details.",
  },
  {
    q: "Are third-party encrypted messengers safe on a Phantom Industries device?",
    a: "Yes. Any modern end-to-end encrypted messenger runs natively on GrapheneOS. They cannot be read by us or by any third party — the encryption keys never leave your device, and messages are stored encrypted at rest. For our own private launch coming soon, see Phantom X.",
  },
];

export const FAQS = [
  {
    q: "Is this legal in Australia?",
    a: "Yes. GrapheneOS is fully legal to install and run. You own the device outright and control every layer of it.",
  },
  {
    q: "Do apps still work?",
    a: "Every major end-to-end encrypted messenger, email, chat and voice app runs natively. Sandboxed Google Play is available on Degoogled if you want it, for banking and everyday apps that require it.",
  },
  {
    q: "Can I still make calls and send SMS?",
    a: "Absolutely. Your device is a normal Australian mobile with a SIM card. Encryption is layered on top — nothing is removed from your carrier experience.",
  },
  {
    q: "How is a Phantom build different from just installing GrapheneOS myself?",
    a: "Phantom is a curated hardened profile — custom SELinux policies, network cloaking, decoy vault, kill-switches, and priority support. It's the ceiling of what a Pixel can do.",
  },
  {
    q: "What is your return / warranty policy?",
    a: "12 month workmanship warranty on our installation and configuration. Hardware falls under Google's factory warranty via your receipt.",
  },
  {
    q: "How can I verify the boot integrity of my device?",
    a: "Every device ships with its GrapheneOS Auditor fingerprint printed on the receipt. Cross-check it in the Auditor app on first boot — mismatch means don't trust the device.",
  },
];

export const VIDEO_REVIEWS = [
  {
    name: "James",
    role: "Investigative journalist \u00b7 Melbourne",
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    rating: 5,
    hook: "\"I use it in three war zones.\"",
    quote:
      "I file stories from countries where owning a normal phone can get you interrogated. My Phantom Tier device has made three international trips this year without a single incident. There is genuinely nothing else on the Australian market like this.",
  },
  {
    name: "Priya",
    role: "Cybersecurity engineer \u00b7 Perth",
    image: "https://images.unsplash.com/photo-1519744434498-a0de604df9db?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    rating: 5,
    hook: "\"Most locked-down Pixel I've ever seen.\"",
    quote:
      "I audit hardened mobile devices for a living. Phantom Industries's build is more thorough than what my own team produces internally. Their SELinux policies and network cloaking config are the tightest I've encountered in Australia.",
  },
  {
    name: "Marcus",
    role: "Corporate lawyer \u00b7 Sydney",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    rating: 5,
    hook: "\"Client confidentiality is a duty, not a slogan.\"",
    quote:
      "My whole practice runs Stealth tier now. I recommend Phantom Industries to every corporate lawyer I mentor. When a subpoena comes for your device, this is what you want to be holding.",
  },
  {
    name: "Sam",
    role: "DV survivor \u00b7 Melbourne",
    image: "https://images.unsplash.com/photo-1542458579-bc6f69b5ce6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    rating: 5,
    hook: "\"I sleep with my phone next to me now.\"",
    quote:
      "I needed a phone my ex physically could not access and could not track. Phantom's team walked me through every step directly. My life is different now. That sounds dramatic but it is the honest truth.",
  },
  {
    name: "Ari",
    role: "Activist / organiser \u00b7 Byron Bay",
    image: "https://images.unsplash.com/photo-1738511980236-c670a95a3970?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    rating: 5,
    hook: "\"It's the standard in our circle now.\"",
    quote:
      "Every organiser I know is on Phantom. Multi-profile compartmentalisation is a game-changer for how we work. Different identities for different rooms, none of them connected. Impossible on stock Android or iOS.",
  },
  {
    name: "Nate",
    role: "Ex-defence \u00b7 Canberra",
    image: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    rating: 5,
    hook: "\"Boot fingerprint matched to the character.\"",
    quote:
      "I've been in security clearances my whole career. When my Phantom Industries Pixel arrived, its GrapheneOS Auditor fingerprint matched the one on my receipt exactly. That's the highest compliment I can give a vendor.",
  },
];

export const REVIEW_STATS = {
  total: 1247,
  average: 4.9,
  five_star_pct: 96,
};

export const REVIEW_SOURCES = [
  {
    id: "pgp",
    name: "PGP-Signed Reviews",
    tagline: "Cryptographically verified customer voices",
    rating: 4.9,
    count: 612,
    featured:
      "\"Every phone I've bought from them has left me feeling more in control of my life. Genuinely nothing else like it on the Australian market.\"",
  },
  {
    id: "telegram",
    name: "Telegram-Verified",
    tagline: "Customers we've spoken with directly over encrypted DM",
    rating: 4.9,
    count: 431,
    featured:
      "\"They walked me through the whole setup one-on-one. I was expecting cold techies and got the most patient, caring vendors I've ever dealt with.\"",
  },
  {
    id: "community",
    name: "Privacy Community",
    tagline: "Endorsed by GrapheneOS forums and privacy advocates",
    rating: 5.0,
    count: 204,
    featured:
      "\"Anytime someone asks in our forum 'where do I buy a hardened Pixel in Australia', the answer is the same. Phantom, every time.\"",
  },
];

export const REVIEW_CARDS = [
  {
    initials: "J.M",
    name: "Verified \u00b7 Melbourne",
    role: "Investigative journalist",
    rating: 5,
    text:
      "Three international trips, three encrypted borders. Not once has this phone caused me trouble. It has genuinely changed how I do my job.",
    verified: "PGP",
    when: "2 weeks ago",
  },
  {
    initials: "K.T",
    name: "Verified \u00b7 Perth",
    role: "Cybersecurity engineer",
    rating: 5,
    text:
      "I audit devices for a living. This is the most locked-down Pixel I've seen \u2014 and I've seen a lot. Their Phantom profile is genuinely impressive work.",
    verified: "Telegram",
    when: "1 month ago",
  },
  {
    initials: "S.R",
    name: "Verified \u00b7 Melbourne",
    role: "DV survivor",
    rating: 5,
    text:
      "I was terrified of my ex tracking me. Phantom set me up with a phone he couldn't get into and can't find. I sleep with my phone next to me now, not under my pillow.",
    verified: "Telegram",
    when: "3 weeks ago",
  },
  {
    initials: "R.C",
    name: "Verified \u00b7 Sydney",
    role: "Crypto investor",
    rating: 5,
    text:
      "Cold wallets are worthless if your phone is compromised. My Phantom XL has been my daily driver for 8 months. No regrets, no surprises.",
    verified: "PGP",
    when: "1 week ago",
  },
  {
    initials: "M.A",
    name: "Verified \u00b7 Sydney",
    role: "Corporate lawyer",
    rating: 5,
    text:
      "My whole practice runs Stealth tier now. Client confidentiality is not a slogan; it's a duty. Phantom made it deliverable.",
    verified: "PGP",
    when: "2 months ago",
  },
  {
    initials: "L.B",
    name: "Verified \u00b7 Byron Bay",
    role: "Activist and organiser",
    rating: 5,
    text:
      "Every organiser I know is now on Phantom. Nothing else lets me switch identities the way multi-profile mode does. It's the standard now, quietly.",
    verified: "Telegram",
    when: "5 days ago",
  },
  {
    initials: "A.P",
    name: "Verified \u00b7 Adelaide",
    role: "GP",
    rating: 5,
    text:
      "I recommend Phantom to patients dealing with stalkers. The duress PIN feature alone gives people back control. Life-changing product.",
    verified: "PGP",
    when: "1 month ago",
  },
  {
    initials: "T.D",
    name: "Verified \u00b7 Canberra",
    role: "Ex-defence",
    rating: 5,
    text:
      "Boot fingerprint matched to the character. That's the highest compliment I give a vendor. Would trust them with more.",
    verified: "PGP",
    when: "3 weeks ago",
  },
];

export const TESTIMONIALS = [
  {
    name: "J. — Melbourne",
    role: "Investigative journalist",
    text: "I file stories from countries where a normal phone is a liability. My Phantom Pixel has made three trips without a single incident. There is genuinely nothing else on the market like this.",
  },
  {
    name: "M. — Sydney",
    role: "Corporate lawyer",
    text: "Client confidentiality isn't negotiable. Stealth tier is now the standard issue for my whole practice. My IT guy said it's the most locked-down device he's ever audited.",
  },
  {
    name: "D. — Brisbane",
    role: "High net-worth individual",
    text: "I stopped using iCloud, my banking is compartmentalized, and I know exactly what my phone is doing. That peace of mind is priceless. Two of my friends have already ordered.",
  },
  {
    name: "K. — Perth",
    role: "Cybersecurity engineer",
    text: "I've been running GrapheneOS myself for years. Buying the Phantom build saved me two evenings and their custom hardening profile has stuff I hadn't even thought to configure. Genuinely impressive work.",
  },
  {
    name: "S. — Melbourne",
    role: "Domestic violence survivor",
    text: "I needed a phone my ex couldn't track and couldn't get into. The Phantom team walked me through every step personally. It has genuinely changed my daily life — I don't panic when it buzzes anymore.",
  },
  {
    name: "R. — Sydney",
    role: "Crypto investor",
    text: "Cold wallets and a hardened phone are non-negotiable in this game. My Phantom XL has been my daily driver for six months and I still can't find another vendor doing what these guys do.",
  },
  {
    name: "A. — Adelaide",
    role: "GP and privacy advocate",
    text: "I recommend Phantom to patients dealing with stalkers and abusive partners. The panic-wipe feature and the sealed packaging genuinely help people feel in control again.",
  },
  {
    name: "T. — Canberra",
    role: "Ex-defence, cleared to level 3",
    text: "You can trust these people. That's the highest compliment I give any vendor. The device I received matched the boot fingerprint on the receipt down to the last character.",
  },
  {
    name: "L. — Byron Bay",
    role: "Activist and organiser",
    text: "Every protest organiser in my circle now runs Phantom. Nothing else lets you compartmentalise identities the way the Phantom tier does. It's the standard now, quietly.",
  },
];

export const priceFor = (basePrice, tierId) => {
  const tier = TIERS.find((t) => t.id === tierId);
  if (!tier) return basePrice;
  return basePrice + tier.price_add;
};

// -------------------- Accessories --------------------
// USB drives, cases, merch. No tier system \u2014 they have variants + a flat
// price map so we compute totals the same way as phones.

const A_IMG = {
  usbBlack: "https://images.unsplash.com/photo-1580062329559-9d512477e711?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  usbDrive: "https://images.unsplash.com/photo-1653607240501-92c08ed94c7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  usbBoot: "https://images.unsplash.com/photo-1610414745513-a1f396b2e7c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  faraday: "/img/merch/faraday-pouch.jpg",
  // Real Phantom Industries product photography
  hoodie: "/img/merch/hoodie.jpg",
  hoodieScript: "/img/merch/hoodie-script.jpg",
  tee: "/img/merch/tee.jpg",
  cap: "/img/merch/cap.jpg",
  stickers: "/img/merch/stickers.jpg",
  caseHard: "/img/merch/case-hard.jpg",
};

// category: "usb" | "case" | "merch"
export const ACCESSORIES = [
  // ---------- USBs ----------
  {
    id: "phantom-vault-usb",
    category: "usb",
    name: "Phantom Vault USB",
    tagline: "Hardware-encrypted, AES-256.",
    image: A_IMG.usbBlack,
    variantLabel: "Capacity",
    variants: [
      { name: "32 GB", price: 89 },
      { name: "128 GB", price: 149 },
      { name: "256 GB", price: 229 },
    ],
    description:
      "PIN-protected, self-locking, epoxy-potted USB drive. Ten wrong PINs and the crypto keys are wiped. Airport-safe. Court-safe.",
  },
  {
    id: "phantom-tails-usb",
    category: "usb",
    name: "Tails OS USB",
    tagline: "Amnesic. Tor-native. Forget-everything OS.",
    image: A_IMG.usbBoot,
    variantLabel: "Capacity",
    variants: [
      { name: "32 GB", price: 79 },
      { name: "64 GB", price: 99 },
      { name: "128 GB", price: 139 },
    ],
    description:
      "Pre-flashed with the latest signed Tails OS release. Boot from USB on any laptop; nothing writes to disk; every session vanishes. Includes persistence guide.",
  },
  {
    id: "phantom-os-usb",
    category: "usb",
    name: "Phantom Dark-Web OS USB",
    tagline: "Whonix + Tails + hardened tooling.",
    image: A_IMG.usbDrive,
    variantLabel: "Capacity",
    variants: [
      { name: "64 GB", price: 179 },
      { name: "128 GB", price: 229 },
      { name: "256 GB", price: 299 },
    ],
    description:
      "Our own live-OS bundle: hardened Whonix workstation + gateway, Monero wallet, PGP toolkit, Molly, KeePassXC, Onion Browser. Boots on any x86 laptop. Never leaves a trace.",
    popular: true,
  },

  // ---------- Cases ----------
  {
    id: "phantom-hard-case",
    category: "case",
    hasStyle: true,
    name: "Phantom Hard Case",
    tagline: "Debossed hood mark. Tactical grip. Every Pixel.",
    image: A_IMG.caseHard,
    variantLabel: "Model",
    variants: [
      { name: "Pixel 10 Pro XL", price: 49 },
      { name: "Pixel 10 Pro", price: 49 },
      { name: "Pixel 10", price: 45 },
      { name: "Pixel 9 Pro XL", price: 49 },
      { name: "Pixel 9 Pro", price: 49 },
      { name: "Pixel 9", price: 45 },
      { name: "Pixel 8 Pro", price: 45 },
      { name: "Pixel 8", price: 45 },
      { name: "Pixel 7 Pro", price: 39 },
      { name: "Pixel 7", price: 39 },
    ],
    description:
      "Two-piece armoured shell with soft-touch coating, raised camera lip and side-grip texturing. Deep-debossed hood mark and wordmark on the back. Ships in tamper-evident packaging.",
  },
  {
    id: "phantom-faraday-pouch",
    category: "case",
    name: "Faraday Signal-Blocking Pouch",
    tagline: "Kill every radio. Instantly.",
    image: A_IMG.faraday,
    variantLabel: "Size",
    variants: [
      { name: "Phone", price: 79 },
      { name: "Phone + Card", price: 99 },
      { name: "Tablet", price: 129 },
    ],
    description:
      "Double-layer RF-blocking fabric with velcro closure and inner tab. Blocks LTE, 5G, Wi-Fi, Bluetooth, GPS, RFID and NFC. Independently tested to \u226180 dB attenuation.",
  },

  // ---------- Merch ----------
  {
    id: "phantom-tee",
    category: "merch",
    hasStyle: true,
    name: "Phantom Tee",
    tagline: "Heavyweight black. Dripping purple wordmark.",
    image: A_IMG.tee,
    variantLabel: "Size",
    variants: [
      { name: "S", price: 45 },
      { name: "M", price: 45 },
      { name: "L", price: 45 },
      { name: "XL", price: 49 },
      { name: "2XL", price: 55 },
    ],
    description:
      "240gsm combed cotton. Full-front dripping-purple wordmark with skull motif. Woven neck-tag hood mark. Boxy modern fit. Made in Melbourne in small batches.",
  },
  {
    id: "phantom-hoodie",
    category: "merch",
    hasStyle: true,
    name: "Phantom Hoodie",
    tagline: "The unofficial uniform.",
    image: A_IMG.hoodie,
    variantLabel: "Size",
    variants: [
      { name: "S", price: 129 },
      { name: "M", price: 129 },
      { name: "L", price: 129 },
      { name: "XL", price: 135 },
      { name: "2XL", price: 149 },
    ],
    description:
      "Heavyweight 400gsm brushed-fleece hoodie. Kangaroo pocket, ribbed cuffs, dropped shoulders. Chest-printed hooded figure and dripping-purple wordmark with skull.",
  },
  {
    id: "phantom-cap",
    category: "merch",
    hasStyle: true,
    name: "Phantom Cap",
    tagline: "Six-panel. Low profile. Embroidered hood.",
    image: A_IMG.cap,
    variantLabel: "Style",
    variants: [
      { name: "Curved brim", price: 39 },
      { name: "Flat brim", price: 39 },
    ],
    description:
      "Structured six-panel cap in matte black with 3D-embroidered purple hooded-figure mark on the front. Adjustable rear strap.",
  },
  {
    id: "phantom-sticker-pack",
    category: "merch",
    hasStyle: false,
    name: "Phantom Sticker Pack",
    tagline: "10 random die-cut vinyl designs. Weatherproof.",
    image: A_IMG.stickers,
    variantLabel: "Pack",
    variants: [
      { name: "Pack of 10 (random)", price: 25 },
    ],
    description:
      "A curated pack of 10 randomly-picked die-cut vinyl stickers from our full artwork library \u2014 dripping-purple wordmarks, hooded figures and skull marks. UV-resistant, weatherproof, laptop-safe. Every pack is different.",
  },
];

export const CATEGORIES = [
  { id: "phones", label: "Phones" },
  { id: "usb", label: "USBs" },
  { id: "case", label: "Cases" },
  { id: "merch", label: "Merch" },
];

export const getAccessory = (id) => ACCESSORIES.find((a) => a.id === id);
export const isAccessory = (id) => !!getAccessory(id);

export const formatAUD = (n) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n);
